import React, { useEffect, useState, useRef } from 'react';
interface LazySectionProps {
  children: ReactNode;
  /** Estimated height in px — prevents CLS while placeholder is shown */
  estimatedHeight: number;
  /** Background color of placeholder — should match section bg to avoid flash */
  placeholderBg?: string;
  /** How far before viewport to start loading (default 900px for mobile) */
  rootMargin?: string;
}
/**
 * Defers rendering of children until the section is near the viewport.
 * Uses IntersectionObserver with a generous rootMargin so content is ready
 * before the user scrolls to it. Once revealed, never unmounts.
 * The placeholder reserves exact space to guarantee CLS = 0.
 */
export function LazySection({
  children,
  estimatedHeight,
  placeholderBg = '#111111',
  rootMargin = '900px'
}: LazySectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const placeholderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Already visible — no observer needed
    if (isVisible) return;
    const el = placeholderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);
  if (!isVisible) {
    return <div ref={placeholderRef} style={{
      minHeight: estimatedHeight,
      backgroundColor: placeholderBg,
      width: '100%'
    }} aria-hidden="true" data-id="element-143" />;
  }
  return <>{children}</>;
}